import {
  Button,
  Empty as AntdEmpty,
  Flex,
  theme,
  Typography,
  type EmptyProps as AntdEmptyProps,
} from 'antd';
import { isValidElement, type ReactNode } from 'react';

import EmptyIconSvg from '@/icons/empty.svg?react';

type EmptySize = 'default' | 'small';

const EMPTY_ICON_DIMENSION: Record<EmptySize, number> = {
  default: 120,
  small: 40,
};

export type EmptyListingConfig = {
  description: string;
  clearLabel?: string;
};

type EmptyIconProps = {
  size?: EmptySize;
};

function EmptyIcon({ size = 'default' }: EmptyIconProps) {
  const { token } = theme.useToken();
  const dimension = EMPTY_ICON_DIMENSION[size];

  return (
    <EmptyIconSvg
      width={dimension}
      height={dimension}
      /* Faded watermark — full-strength brand green reads as an action button. */
      style={{ color: token.colorPrimary, opacity: 0.45 }}
    />
  );
}

function renderDescription(description: ReactNode): ReactNode {
  if (typeof description === 'string') {
    return (
      <Flex vertical align="center" gap={4}>
        <Typography.Text type="secondary">{description}</Typography.Text>
      </Flex>
    );
  }

  if (isValidElement(description)) {
    return description;
  }

  return description;
}

type EmptyProps = AntdEmptyProps & {
  size?: EmptySize;
  clearLabel?: string;
  showClear?: boolean;
  onClear?: () => void;
};

function Empty({
  size = 'default',
  image,
  description,
  children,
  clearLabel,
  showClear,
  onClear,
  ...props
}: EmptyProps) {
  const clearAction =
    children ??
    (showClear && clearLabel && onClear ? (
      <Button size="middle" onClick={onClear}>
        {clearLabel}
      </Button>
    ) : null);

  return (
    <AntdEmpty
      image={image ?? <EmptyIcon size={size} />}
      description={
        description != null ? renderDescription(description) : description
      }
      {...props}
    >
      {clearAction}
    </AntdEmpty>
  );
}

export default Empty;
